import { Company } from 'module/api/company/types';
import { QueryCombinerType, SimpleSearchFilter } from 'module/api/filter/types';
import { SourceType } from 'module/api/filter/types';
import { fieldToFilterableField } from 'module/helpers/filter/type-transformers';
import { useTranslation } from 'module/hooks/use-translation';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Popover } from 'module/ui/components/atoms/popover';
import { FilterBar } from 'module/ui/components/molecules/filter-bar';
import { HelperText01, Short01 } from 'module/ui/components/styles';
import { faEnvelope } from '@fortawesome/pro-duotone-svg-icons';
import {
  faArrowsFromDottedLine,
  faArrowsToDottedLine,
  faChevronDown,
  faEdit,
  faSidebar,
  faSparkles,
  faPage,
  faEnvelope as faEnvelopeRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faEllipsisVertical,
  faSignal,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useParams } from '@rocketlane/rocket-router';
import {
  Button,
  EmptyState as RocketlaneEmptyState,
  Icon,
} from '@rocketlane/ui';
import { Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { PageType } from 'api/page-template/types';
import { useSignalFieldsAsFilterableFieldWithReason } from 'helpers/fields/signal-fields/fields';
import moment from 'moment';
import { useSearchEmailsScrollPagination } from 'queries/emails';
import { useGetAllEmailFieldsQuery } from 'queries/fields';
import { useDeletePage } from 'queries/pages/use-delete-page';
import { useEmailTimelineQuery } from 'queries/signal-occurrences';
import React from 'react';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { MetadataTooltip } from 'components/common/metadata-tooltip';
import {
  LoadingWrapper,
  Sidebar,
  SidebarInPopover,
  PageWrapper,
  PageContent,
  MainContent,
  SecondaryFilter,
  SidebarMenu,
} from 'components/organisms/pages/shared/styles';
import { EmailSignalsSidebar } from './email-signals-sidebar';
import { useEmailPagesRouteNavigations } from './route-navigations';
import { useCurrentEmailPage } from './use-current-email-page';
import { useEmailPagesList } from './use-email-pages-list';
import { ChatProvider, useChatContext } from '../chat/chat-context';
import { ChatSidebar } from '../chat/chat-sidebar';
import { useChatTrigger } from '../chat/use-chat-trigger';
import { Document } from '../document/document';
import { RenderEmailAttachment } from '../email-transcript/email-attachment';
import {
  EmailTranscript,
  EmailTranscriptHandle,
} from '../email-transcript/email-transcript';
import { MeetingPagesSidePane } from '../meeting-pages/side-pane';

const RocketlaneEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: var(--spacing-09) var(--spacing-08);
`;

const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const EmailSidebarMenu = styled(SidebarMenu)``;

// v2-only sidebar containers: disable outer scroll/padding so the inner
// flex layout controls both the fixed search header and the scrollable list.
const EmailV2Sidebar = styled(Sidebar)`
  padding: 0 !important;
  overflow: hidden !important;
  overflow-y: hidden !important;
`;

const EmailV2SidebarInPopover = styled(SidebarInPopover)`
  padding: 0 !important;
  overflow: hidden !important;
  overflow-y: hidden !important;
`;

const SidebarContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SidebarSearchSection = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-05) var(--spacing-05) var(--spacing-03);
  background: var(--white-100);
`;

const SidebarEmailList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 var(--spacing-05) var(--spacing-05);
`;

const BannerCenterWrapper = styled.div`
  padding: var(--spacing-07) var(--spacing-09) var(--spacing-05);
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -16px;
    height: 16px;
    background: linear-gradient(
      to bottom,
      var(--white-100, #fff) 0%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const BannerCenterInner = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
`;

/* Matches Scroll's left/right padding so the inner element can replicate the
   same max-width:1500px centering that EmailTranscript's Content div uses. */
const EmailActionBarTitleWrapper = styled.div`
  position: absolute;
  left: var(--spacing-09);
  right: var(--spacing-09);
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

/* Mirrors EmailTranscript's Content div so the text tracks exactly where the
   sender name starts: max-width centering + avatar(32px) + gap. */
const EmailActionBarTitleInner = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding-left: calc(3px + var(--spacing-04));
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SummaryLabel = styled.span`
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
`;

const SummaryCard = styled.button<{ $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $expanded }) => ($expanded ? '100%' : '220px')};
  border-radius: var(--Radius-8, 8px);
  background: var(--Default-Blue-10, #edf5ff);
  border: none;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  transition:
    width 250ms ease-in-out,
    background 150ms ease;

  &:hover {
    background: var(--blue-20, #daeaff);
  }
`;

const SummaryCardHeader = styled.div`
  display: flex;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--spacing-05);
  flex-shrink: 0;
`;

const SummaryCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const SummaryChevron = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--icon-secondary);
  transition: transform 250ms ease-in-out;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
`;

const SummaryCardBody = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 250ms ease-in-out;
`;

/* Must have overflow:hidden and NO padding so grid-template-rows:0fr collapses to true zero. */
const SummaryCardBodyInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const SummaryCardBodyContent = styled.div`
  padding: 0 var(--spacing-05) 12px;
`;

const SummaryText = styled.p`
  margin: 0;
  font-size: var(--font-size-base);
  color: var(--text-primary);
  line-height: var(--line-height-05);
`;

enum EmailPagesRightSidebarKeys {
  Signals = 'signals',
}

const EmailListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow: visible;
  justify-content: center;
  gap: var(--spacing-01);
`;
const EmailListItemHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--spacing-02);
  width: 100%;
  min-width: 0;
`;
const EmailListItemTitle = styled.div`
  ${Short01};
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EmailListItemDate = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
  flex-shrink: 0;
  white-space: nowrap;
`;
const EmailListItemPreview = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Number of threads fetched on the initial load.
const EMAILS_INITIAL_PAGE_SIZE = 25;
// Number of threads fetched on each subsequent scroll page.
const EMAILS_PAGE_SIZE = 10;
// Distance (px) from the list bottom at which we prefetch the next page.
const INFINITE_SCROLL_THRESHOLD = 200;

export type EmailPagesPermissions = {
  canReadEmailPages: boolean;
  canWriteEmailPages?: boolean;
  canDeleteEmailPages: boolean;
  canReadEmailPageTemplates: boolean;
  canCreateEmailPageTemplates: boolean;
  canWriteEmailPageTemplates: boolean;
  canDeleteEmailPageTemplates: boolean;
  canWriteEmailIntelligenceSettings?: boolean;
};

interface AccountEmailsProps {
  company: Company;
  projectId?: number;
  emailPermissions?: EmailPagesPermissions;
  isModuleFed?: boolean;
  renderAttachment?: RenderEmailAttachment;
  hostApi?: {
    renderComponent: (request: {
      type: string;
      props: Record<string, any>;
    }) => React.ReactNode;
    hasFeatures?: (feature: string) => boolean;
    searchText?: string;
    onSearchTextChange?: (text: string) => void;
  };
  filterPayload?: SimpleSearchFilter;
  onClearFilter?: () => void;
  emailFields?: unknown[];
  emailMessageId?: number;
}

const AccountEmailsContentV1 = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
}: AccountEmailsProps) => {
  const intl = useIntl();
  const { value, onChange, clear } = useSearchValueState();
  const { emailPages, isEmailPagesLoading, allEmailPages } = useEmailPagesList({
    company,
    searchText: value,
  });
  const { emailPage, isEmailPageLoading } = useCurrentEmailPage({
    company,
    emailPages,
    isEmailPagesLoading,
    projectId,
  });

  const { navigateToList, navigateToEmail, navigateToEmailEdit } =
    useEmailPagesRouteNavigations(projectId);

  const emailPageDocumentMappingId =
    emailPage?.metadata.documentSourceMappingModel?.pageDocumentMappingId;
  const emailDocumentId = emailPage?.metadata.documentId;

  // Use allEmailPages (full dataset), not emailPages (search-filtered).
  // With active search, emailPages can be empty despite other pages existing,
  // which would incorrectly trigger navigateToList and drop context.
  const allEmailAssociations = useMemo(
    () => allEmailPages.flatMap((page) => page.associations),
    [allEmailPages],
  );

  const { handleDelete } = useDeletePage({
    pageDocumentMappingId: emailPageDocumentMappingId,
    documentId: emailDocumentId,
    pageType: PageType.Email,
    companyId: projectId ?? company.companyId,
    allPages: allEmailAssociations,
    navigateToPage: navigateToEmail,
    navigateToList: navigateToList,
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { isChatOpen, setIsChatOpen, chatData, setChatData } = useChatContext();

  useChatTrigger();

  const handleChatDataUsed = useCallback(() => {
    if (chatData) {
      setChatData({
        ...chatData,
        elementContext: '',
      });
    }
  }, [setChatData, chatData]);

  const sidebarItems = useMemo((): ItemType[] => {
    if (!emailPages?.length) {
      return [];
    }

    return emailPages.flatMap((page) =>
      page.associations.map((association) => ({
        label: (
          <EmailListItemContent>
            <EmailListItemTitle>{association.title}</EmailListItemTitle>
            <EmailListItemDate>
              {association.updatedAt
                ? humanise(moment(association.updatedAt))
                : ''}
            </EmailListItemDate>
          </EmailListItemContent>
        ),
        key: String(association.documentId),
        icon: <FontAwesomeIcon icon={faPage} size={12} />,
      })),
    );
  }, [emailPages]);

  const selectedAssociationKey =
    emailPage?.metadata?.documentId?.toString() || '';

  React.useEffect(() => {
    setIsChatOpen(false);
  }, [selectedAssociationKey, setIsChatOpen]);

  const searchPlaceholder = useTranslation('search');
  const SideBar = useMemo(() => {
    return (
      <>
        <div style={{ marginBottom: 'var(--spacing-03)' }}>
          <SearchInputExpanded
            placeholder={searchPlaceholder}
            value={value}
            onChange={onChange}
            onClear={clear}
          />
        </div>
        <div className="flex flex-col">
          <SidebarMenu
            items={sidebarItems}
            onSelect={({ key }) => {
              const companyIdForNavigation = projectId ?? company.companyId;

              navigateToEmail(companyIdForNavigation, Number(key));
            }}
            selectedKeys={[selectedAssociationKey]}
            mode="inline"
          />
        </div>
      </>
    );
  }, [
    searchPlaceholder,
    value,
    onChange,
    clear,
    sidebarItems,
    selectedAssociationKey,
    navigateToEmail,
    company.companyId,
    projectId,
  ]);

  if (isEmailPagesLoading) {
    return <PageLoading />;
  }

  return (
    <PageWrapper>
      {isSidebarOpen && <Sidebar>{SideBar}</Sidebar>}

      <PageContent>
        <MainContent>
          <FilterBar>
            <FilterBar.Primary>
              {isEmailPagesLoading || allEmailPages?.length === 0 ? null : (
                <Popover
                  content={
                    isSidebarOpen ? null : (
                      <SidebarInPopover>{SideBar}</SidebarInPopover>
                    )
                  }
                  placement="bottomLeft"
                  destroyTooltipOnHide
                  isDisabled={isSidebarOpen}
                  mouseEnterDelay={0.3}
                >
                  <Button
                    variant="ghost"
                    leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  />
                </Popover>
              )}
            </FilterBar.Primary>
            <SecondaryFilter>
              {emailPage != null && !isEmailPageLoading && (
                <>
                  <MetadataTooltip
                    updatedAt={emailPage.metadata.updatedAt}
                    createdAt={emailPage.metadata.createdAt}
                  />

                  {(!isModuleFed || emailPermissions?.canWriteEmailPages) && (
                    <Button
                      variant="primary"
                      leftIcon={<FontAwesomeIcon icon={faEdit} size={12} />}
                      onClick={() => {
                        const companyIdForNavigation =
                          projectId ?? company.companyId;

                        navigateToEmailEdit(
                          companyIdForNavigation,
                          emailPage.metadata.documentId,
                        );
                      }}
                    >
                      <FormattedMessage id="edit" />
                    </Button>
                  )}

                  {(!isModuleFed || emailPermissions?.canDeleteEmailPages) && (
                    <MoreActionsDropdown
                      menu={{
                        onClick: ({ key }) => {
                          if (key === 'delete') {
                            handleDelete();
                          }
                        },
                        items: [
                          {
                            type: 'group',
                            label: <FormattedMessage id="more_actions" />,
                            children: [
                              {
                                key: 'delete',
                                label: <FormattedMessage id="delete" />,
                                icon: <FontAwesomeIcon icon={faTrash} />,
                                danger: true,
                                disabled: !emailPageDocumentMappingId,
                              },
                            ],
                          },
                        ],
                      }}
                      triggerElement={
                        <Button
                          variant="ghost"
                          leftIcon={
                            <FontAwesomeIcon
                              icon={faEllipsisVertical}
                              size={12}
                            />
                          }
                        />
                      }
                    />
                  )}
                </>
              )}
            </SecondaryFilter>
          </FilterBar>

          {isEmailPageLoading && allEmailPages?.length > 0 ? (
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          ) : emailPage == null ? (
            <RocketlaneEmptyStateRoot>
              <RocketlaneEmptyState
                icon={{ icon: faEnvelope }}
                message={intl.formatMessage({
                  id: 'email_page.empty_state_title',
                })}
                description={intl.formatMessage({
                  id: 'email_page.empty_state_message',
                })}
              />
            </RocketlaneEmptyStateRoot>
          ) : (
            <Document
              isChatOpen={isChatOpen}
              document={emailPage}
              readonly={true}
              pageType={PageType.Email}
              sourceId={
                emailPage?.metadata?.documentSourceMappingModel?.sourceId
              }
            />
          )}
        </MainContent>
      </PageContent>

      <ChatSidebar
        isOpen={isChatOpen}
        title={emailPage?.metadata.title}
        accountId={company.companyId}
        userId={303}
        chatData={chatData}
        onChatDataUsed={handleChatDataUsed}
        onClose={() => setIsChatOpen(false)}
      />
    </PageWrapper>
  );
};

const AccountEmailsContentV2 = ({
  company,
  projectId,
  renderAttachment,
  hostApi,
  filterPayload,
  emailMessageId,
}: AccountEmailsProps) => {
  const intl = useIntl();
  const searchText = hostApi?.searchText ?? '';
  const onSearchTextChange = hostApi?.onSearchTextChange;

  const { fields: signalFields } = useSignalFieldsAsFilterableFieldWithReason();

  const { data: rawEmailFields = [] } = useGetAllEmailFieldsQuery();
  const emailFields = useMemo(
    () =>
      rawEmailFields.map((f) => fieldToFilterableField(f, SourceType.Email)),
    [rawEmailFields],
  );

  const activeFilter: SimpleSearchFilter = filterPayload ?? {
    match: QueryCombinerType.All,
  };

  const {
    emails,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSearchEmailsScrollPagination(activeFilter, {
    enabled: true,
    limit: EMAILS_PAGE_SIZE,
    initialLimit: EMAILS_INITIAL_PAGE_SIZE,
    companyId: company.companyId,
  });

  // After the first successful load, never show the full-page loader again —
  // filter refetches show inline loaders in the sidebar and content area instead.
  const initialLoadComplete = useRef(false);
  // Ref on the sidebar scroll container — used to detect when the initial
  // page doesn't fill the visible area so we can prefetch the next page.
  const sidebarRef = useRef<HTMLDivElement>(null);

  if (!initialLoadComplete.current && !isLoading) {
    initialLoadComplete.current = true;
  }

  const { navigateToEmail } = useEmailPagesRouteNavigations(projectId, true);

  const { emailId: emailIdParam, emailPageId: emailPageIdParam } = useParams<{
    emailId?: string;
    emailPageId?: string;
  }>();
  const selectedThreadIdParam = emailIdParam ?? emailPageIdParam;
  const selectedThreadId =
    selectedThreadIdParam != null ? Number(selectedThreadIdParam) : NaN;
  const isThreadSelected = !Number.isNaN(selectedThreadId);

  const sourceIdForTranscript = isThreadSelected ? selectedThreadId : undefined;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { setIsChatOpen } = useChatContext();

  useChatTrigger();

  // Fetch signals for the selected email thread
  const { data: emailTimeline, isLoading: isSignalsLoading } =
    useEmailTimelineQuery(company.companyId, sourceIdForTranscript);

  const hasSummary = !!(
    emailTimeline?.summary || emailTimeline?.signalAwareRecap
  );
  const showSummaryBanner =
    !isSignalsLoading && sourceIdForTranscript != null && hasSummary;

  const transcriptRef = useRef<EmailTranscriptHandle>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  useEffect(() => {
    setIsAllExpanded(false);
  }, [sourceIdForTranscript]);

  // Right side pane
  const [activeRightSidebarKey, setActiveRightSidebarKey] =
    useState<EmailPagesRightSidebarKeys>();
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [retainedSubject, setRetainedSubject] = useState<string | undefined>();
  const [isListPopoverOpen, setIsListPopoverOpen] = useState(false);

  const openRightSideBar = useCallback((key: EmailPagesRightSidebarKeys) => {
    setActiveRightSidebarKey(key);
    setIsSidebarOpen(false);
    setIsListPopoverOpen(false);
  }, []);

  const handleCloseRightSidebar = useCallback(() => {
    setActiveRightSidebarKey(undefined);
  }, []);

  const selectedAssociationKey = isThreadSelected
    ? String(selectedThreadId)
    : '';

  const selectedThread = useMemo(
    () => emails.find((email) => email.emailThreadId === selectedThreadId),
    [emails, selectedThreadId],
  );

  // When the selected thread changes, close chat, reset the side pane, and close list popover.
  useEffect(() => {
    setIsChatOpen(false);
    setActiveRightSidebarKey(undefined);
    setIsSummaryExpanded(false);
    setRetainedSubject(undefined);
    setIsListPopoverOpen(false);
  }, [selectedAssociationKey, setIsChatOpen]);

  // Persist the subject so it survives filter-induced gaps where selectedThread
  // drops out of the filtered list and emailTimeline hasn't loaded yet.
  const resolvedSubject =
    selectedThread?.subject || emailTimeline?.source?.title;

  useEffect(() => {
    if (resolvedSubject) {
      setRetainedSubject(resolvedSubject);
    }
  }, [resolvedSubject]);

  const rightSidebarItems = useMemo(
    () => [
      {
        key: EmailPagesRightSidebarKeys.Signals,
        title: 'Signals',
        icon: <Icon icon={faSignal} size="1x" />,
        content: (
          <EmailSignalsSidebar
            timeline={emailTimeline}
            isLoading={isSignalsLoading}
          />
        ),
      },
    ],
    [emailTimeline, isSignalsLoading],
  );

  useEffect(() => {
    // Only auto-select when no thread is selected yet — never navigate on
    // filter changes while a thread is already open.
    if (isFetching || isLoading || isThreadSelected) {
      return;
    }
    const firstThread = emails[0];

    if (firstThread) {
      navigateToEmail(
        projectId ?? company.companyId,
        firstThread.emailThreadId,
      );
    }
  }, [
    isFetching,
    isLoading,
    isThreadSelected,
    emails,
    navigateToEmail,
    projectId,
    company.companyId,
  ]);

  const sidebarItems = useMemo(
    (): ItemType[] =>
      emails.map((email) => ({
        label: (
          <EmailListItemContent>
            <EmailListItemHeader>
              <EmailListItemTitle>{email.subject}</EmailListItemTitle>
              <EmailListItemDate>
                {email.updatedAt ? moment(email.updatedAt).format('D MMM') : ''}
              </EmailListItemDate>
            </EmailListItemHeader>
            {email.latestMessagePreview && (
              <EmailListItemPreview>
                {email.latestMessagePreview}
              </EmailListItemPreview>
            )}
          </EmailListItemContent>
        ),
        key: String(email.emailThreadId),
        icon: <FontAwesomeIcon icon={faEnvelopeRegular} size={12} />,
      })),
    [emails],
  );

  const handleSidebarSelect = useCallback(
    (key: string) => {
      navigateToEmail(projectId ?? company.companyId, Number(key));
    },
    [company.companyId, projectId, navigateToEmail],
  );

  const handleListScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      const el = event.currentTarget;
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

      if (distanceToBottom < INFINITE_SCROLL_THRESHOLD) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // After each page completes, check whether the loaded threads fill the
  // sidebar's visible height. If there's still empty space and more pages
  // exist, fetch the next page automatically — without waiting for the
  // user to scroll.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    const el = sidebarRef.current;

    if (!el) {
      return;
    }
    if (el.scrollHeight <= el.clientHeight) {
      fetchNextPage();
    }
  }, [emails.length, isFetchingNextPage, hasNextPage, fetchNextPage]);

  const searchPlaceholder = useTranslation('search');
  const SideBar = useMemo(
    () => (
      <SidebarContentLayout>
        <SidebarSearchSection>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <SearchInputExpanded
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={(e) => onSearchTextChange?.(e.target.value)}
              onClear={() => onSearchTextChange?.('')}
            />
            {hostApi?.renderComponent({
              type: 'email_inbox_filter',
              props: { signalFields, emailFields },
            })}
          </div>
        </SidebarSearchSection>
        {isLoading || (isFetching && !isFetchingNextPage) ? (
          <SidebarEmailList>
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          </SidebarEmailList>
        ) : (
          <SidebarEmailList ref={sidebarRef} onScroll={handleListScroll}>
            <EmailSidebarMenu
              items={sidebarItems}
              onSelect={({ key }) => handleSidebarSelect(String(key))}
              selectedKeys={[selectedAssociationKey]}
              mode="inline"
            />
          </SidebarEmailList>
        )}
      </SidebarContentLayout>
    ),
    [
      searchPlaceholder,
      searchText,
      onSearchTextChange,
      isLoading,
      isFetching,
      isFetchingNextPage,
      sidebarItems,
      selectedAssociationKey,
      handleSidebarSelect,
      hostApi,
      signalFields,
      emailFields,
      sidebarRef,
      handleListScroll,
    ],
  );

  if (isLoading && !initialLoadComplete.current) {
    return <PageLoading />;
  }

  return (
    <PageWrapper>
      {isSidebarOpen && <EmailV2Sidebar>{SideBar}</EmailV2Sidebar>}

      <PageContent>
        <MainContent>
          <FilterBar>
            <FilterBar.Primary>
              {emails.length === 0 && filterPayload == null ? null : (
                <Popover
                  content={
                    isSidebarOpen ? null : (
                      <EmailV2SidebarInPopover>
                        {SideBar}
                      </EmailV2SidebarInPopover>
                    )
                  }
                  placement="bottomLeft"
                  destroyTooltipOnHide
                  isDisabled={isSidebarOpen || activeRightSidebarKey == null}
                  open={!isSidebarOpen && isListPopoverOpen}
                  onOpenChange={(open) => {
                    if (!isSidebarOpen) {
                      setIsListPopoverOpen(open);
                    }
                  }}
                  trigger="click"
                >
                  <Button
                    variant="ghost"
                    leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                    onClick={() => {
                      if (activeRightSidebarKey == null) {
                        setIsSidebarOpen(!isSidebarOpen);
                      }
                    }}
                  />
                </Popover>
              )}
            </FilterBar.Primary>
            {(resolvedSubject || retainedSubject) && (
              <EmailActionBarTitleWrapper>
                <EmailActionBarTitleInner>
                  {resolvedSubject || retainedSubject}
                </EmailActionBarTitleInner>
              </EmailActionBarTitleWrapper>
            )}
            {sourceIdForTranscript != null && (
              <FilterBar.Secondary>
                <Tooltip
                  title={isAllExpanded ? 'Collapse all' : 'Expand all'}
                  placement="bottom"
                >
                  <Button
                    variant="ghost"
                    leftIcon={
                      <FontAwesomeIcon
                        icon={
                          isAllExpanded
                            ? faArrowsToDottedLine
                            : faArrowsFromDottedLine
                        }
                        size={12}
                      />
                    }
                    onClick={() => {
                      if (isAllExpanded) {
                        transcriptRef.current?.collapseAll();
                        setIsAllExpanded(false);
                      } else {
                        transcriptRef.current?.expandAll();
                        setIsAllExpanded(true);
                      }
                    }}
                  />
                </Tooltip>
              </FilterBar.Secondary>
            )}
          </FilterBar>

          {showSummaryBanner && (
            <BannerCenterWrapper>
              <BannerCenterInner>
                <SummaryCard
                  type="button"
                  $expanded={isSummaryExpanded}
                  onClick={() => setIsSummaryExpanded((v) => !v)}
                >
                  <SummaryCardHeader>
                    <SummaryCardHeaderLeft>
                      <FontAwesomeIcon icon={faSparkles} size={14} />
                      <SummaryLabel>Summary</SummaryLabel>
                    </SummaryCardHeaderLeft>
                    <SummaryChevron $expanded={isSummaryExpanded}>
                      <FontAwesomeIcon icon={faChevronDown} size={12} />
                    </SummaryChevron>
                  </SummaryCardHeader>
                  <SummaryCardBody $expanded={isSummaryExpanded}>
                    <SummaryCardBodyInner>
                      <SummaryCardBodyContent>
                        <SummaryText>
                          {emailTimeline?.signalAwareRecap ||
                            emailTimeline?.summary}
                        </SummaryText>
                      </SummaryCardBodyContent>
                    </SummaryCardBodyInner>
                  </SummaryCardBody>
                </SummaryCard>
              </BannerCenterInner>
            </BannerCenterWrapper>
          )}

          {isLoading && !initialLoadComplete.current ? (
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          ) : emails.length === 0 && !isFetching && !isThreadSelected ? (
            <RocketlaneEmptyStateRoot>
              <RocketlaneEmptyState
                icon={{ icon: faEnvelope }}
                message={intl.formatMessage({
                  id: 'email_page.empty_state_title',
                })}
                description={intl.formatMessage({
                  id: 'email_page.empty_state_message',
                })}
              />
            </RocketlaneEmptyStateRoot>
          ) : (
            <TabContent>
              {sourceIdForTranscript != null ? (
                <EmailTranscript
                  ref={transcriptRef}
                  sourceId={sourceIdForTranscript}
                  renderAttachment={renderAttachment}
                  emailMessageId={emailMessageId}
                  onIframeClick={() => setIsListPopoverOpen(false)}
                />
              ) : (
                <LoadingWrapper>
                  <Loading size="large" />
                </LoadingWrapper>
              )}
            </TabContent>
          )}
        </MainContent>
      </PageContent>

      <MeetingPagesSidePane
        items={rightSidebarItems}
        activeKey={activeRightSidebarKey}
        onSelect={(key) => {
          if (key === activeRightSidebarKey) {
            handleCloseRightSidebar();
          } else {
            openRightSideBar(key as EmailPagesRightSidebarKeys);
          }
        }}
        onClose={handleCloseRightSidebar}
      />
    </PageWrapper>
  );
};

const AccountEmailsContent = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed,
  renderAttachment,
  hostApi,
  filterPayload,
  onClearFilter,
  emailFields,
  emailMessageId,
}: AccountEmailsProps) => {
  return (hostApi?.hasFeatures?.('emailPagesV2') ?? false) ? (
    <AccountEmailsContentV2
      company={company}
      projectId={projectId}
      emailPermissions={emailPermissions}
      isModuleFed={isModuleFed}
      renderAttachment={renderAttachment}
      hostApi={hostApi}
      filterPayload={filterPayload}
      onClearFilter={onClearFilter}
      emailFields={emailFields}
      emailMessageId={emailMessageId}
    />
  ) : (
    <AccountEmailsContentV1
      company={company}
      projectId={projectId}
      emailPermissions={emailPermissions}
      isModuleFed={isModuleFed}
      renderAttachment={renderAttachment}
    />
  );
};

export const AccountEmails = ({
  company,
  projectId,
  emailPermissions,
  isModuleFed = true,
  renderAttachment,
  hostApi,
  filterPayload,
  onClearFilter,
  emailFields,
  emailMessageId,
}: AccountEmailsProps) => {
  return (
    <ChatProvider>
      <AccountEmailsContent
        company={company}
        projectId={projectId}
        emailPermissions={emailPermissions}
        isModuleFed={isModuleFed}
        renderAttachment={renderAttachment}
        hostApi={hostApi}
        filterPayload={filterPayload}
        onClearFilter={onClearFilter}
        emailFields={emailFields}
        emailMessageId={emailMessageId}
      />
    </ChatProvider>
  );
};
